import {
  ActionIcon,
  Badge,
  Box,
  Collapse,
  Group,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertTriangle, IconChevronRight } from '@tabler/icons-react'
import { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DeploymentStatus } from '@/types/deployment'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { DeploymentErrorSpoiler } from '../DeploymentList/DeploymentErrorSpoiler'
import { DeploymentStatusIndicator } from '../DeploymentList/DeploymentStatusIndicator/DeploymentStatusIndicator'
import { GroupMemberTable } from './GroupMemberTable'
import { MemberStatusCount } from './MemberStatusCount'
import {
  MemberSummary,
  groupContainsStream,
  summarizeMembers
} from './deploymentTreeHelpers'
import { DeploymentGroupRowSlots } from './deploymentTreeTypes'

const COLUMN_COUNT = 4

const CAMERAS_RUNNING_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.Running,
  DeploymentStatus.ConnectingVideoStream,
  DeploymentStatus.Degraded
])

const getStatusDetail = (group: DeploymentGroup, summary: MemberSummary) => {
  if (
    group.status === DeploymentStatus.QueuedForEngineBuild &&
    group.engine.queue_position !== null
  ) {
    return (
      <FormattedMessage
        id="deployments.status.detail.queuedForEngineBuild.position"
        values={{ position: group.engine.queue_position }}
      />
    )
  }

  if (group.status === DeploymentStatus.BuildingEngine && group.gpu) {
    return (
      <FormattedMessage
        id="deployments.list.group.onGpu"
        values={{ index: group.gpu.index }}
      />
    )
  }

  if (CAMERAS_RUNNING_STATUSES.has(group.status)) {
    return (
      <FormattedMessage
        id="deployments.status.detail.camerasRunning"
        values={{ running: summary.running, total: summary.total }}
      />
    )
  }

  return undefined
}

type GroupGpuCellProps = {
  group: DeploymentGroup
}

const GroupGpuCell = ({ group }: GroupGpuCellProps) => {
  const { gpu, allowed_gpus } = group

  if (!gpu) {
    return (
      <Tooltip
        disabled={!allowed_gpus?.length}
        label={
          <FormattedMessage
            id="deployments.list.group.allowedGpus"
            values={{ gpus: allowed_gpus?.join(', ') }}
          />
        }
      >
        <Text size="xs" c="dimmed" span>
          <FormattedMessage id="deployments.list.group.gpuNotChosen" />
        </Text>
      </Tooltip>
    )
  }

  return (
    <Group gap={6} wrap="nowrap">
      <Text size="xs">
        <FormattedMessage
          id="deployments.list.group.gpu"
          values={{ index: gpu.index, name: gpu.name }}
        />
      </Text>

      {gpu.pinned_by_override && (
        <Tooltip
          label={
            <FormattedMessage id="deployments.list.group.pinned.tooltip" />
          }
        >
          <Badge size="xs" variant="light" color="gray">
            <FormattedMessage id="deployments.list.group.pinned" />
          </Badge>
        </Tooltip>
      )}
    </Group>
  )
}

type DeploymentGroupRowProps = DeploymentGroupRowSlots & {
  group: DeploymentGroup
  label: number
  device?: Device
  highlightedStreamId?: string
  defaultExpanded?: boolean
}

export const DeploymentGroupRow = ({
  group,
  label,
  device,
  highlightedStreamId,
  defaultExpanded = false,
  renderGroupActions,
  renderMemberActions,
  onOpenGroup
}: DeploymentGroupRowProps) => {
  const intl = useIntl()

  const containsHighlighted =
    !!highlightedStreamId && groupContainsStream(group, highlightedStreamId)

  const [isExpanded, { toggle, open }] = useDisclosure(
    defaultExpanded || containsHighlighted
  )

  useEffect(() => {
    if (containsHighlighted) {
      open()
    }
  }, [containsHighlighted, open])

  const summary = useMemo(
    () => summarizeMembers(group.members),
    [group.members]
  )

  const hasPendingUpdate = group.applied_revision !== group.revision
  const trackingOff = group.warnings.some(
    (warning) => warning.code === 'tracking_off'
  )

  return (
    <>
      <Table.Tr
        style={{ cursor: 'pointer' }}
        onClick={() => (onOpenGroup ? onOpenGroup(group) : toggle())}
      >
        <Table.Td>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-expanded={isExpanded}
              aria-label={intl.formatMessage({
                id: isExpanded
                  ? 'deployments.list.group.toggle.hide'
                  : 'deployments.list.group.toggle.show'
              })}
              onClick={(e) => {
                e.stopPropagation()
                toggle()
              }}
            >
              <IconChevronRight
                size={16}
                style={{
                  transform: isExpanded ? 'rotate(90deg)' : 'none',
                  transition: 'transform 150ms ease'
                }}
              />
            </ActionIcon>

            <Stack gap={4}>
              <Text size="sm" fw={600}>
                <FormattedMessage
                  id="deployments.list.group.label"
                  values={{ number: label }}
                />
              </Text>

              <Text size="xs" c="dimmed">
                <FormattedMessage
                  id="deployments.list.inputSource.cameras"
                  values={{ count: summary.total }}
                />
              </Text>

              <MemberStatusCount summary={summary} />
            </Stack>
          </Group>
        </Table.Td>

        <Table.Td>
          <GroupGpuCell group={group} />
        </Table.Td>

        <Table.Td>
          <DeploymentStatusIndicator
            status={group.status}
            statusUpdatedAt={group.status_updated_at}
            deviceActive={device?.active}
            detail={getStatusDetail(group, summary)}
          />

          <DeploymentErrorSpoiler
            status={group.status}
            errorBody={group.error_body}
          />

          {(group.tracked || hasPendingUpdate || group.warnings.length > 0) && (
            <Group gap={6} mt={4}>
              {group.tracked && (
                <Badge
                  size="xs"
                  variant="light"
                  color={trackingOff ? 'orange' : 'teal'}
                >
                  <FormattedMessage
                    id={
                      trackingOff
                        ? 'deployments.groups.warning.badge.tracking_off'
                        : 'deployments.groups.tracking.badge'
                    }
                  />
                </Badge>
              )}

              {hasPendingUpdate && (
                <Badge size="xs" variant="light" color="blue">
                  <FormattedMessage id="deployments.list.group.updatePending" />
                </Badge>
              )}

              {group.warnings.length > 0 && (
                <Tooltip
                  label={
                    <Stack gap={2}>
                      {group.warnings.map((warning) => (
                        <Text key={warning.code} size="xs">
                          {warning.detail || warning.code}
                        </Text>
                      ))}
                    </Stack>
                  }
                >
                  <Group gap={4} wrap="nowrap">
                    <IconAlertTriangle
                      size={14}
                      color="var(--mantine-color-orange-6)"
                    />
                    <Text size="xs" c="orange">
                      <FormattedMessage
                        id="deployments.list.group.warnings"
                        values={{ count: group.warnings.length }}
                      />
                    </Text>
                  </Group>
                </Tooltip>
              )}
            </Group>
          )}
        </Table.Td>

        <Table.Td onClick={(e) => e.stopPropagation()}>
          {renderGroupActions?.(group)}
        </Table.Td>
      </Table.Tr>

      <Table.Tr>
        <Table.Td colSpan={COLUMN_COUNT} p={0} style={{ borderTop: 'none' }}>
          <Collapse in={isExpanded}>
            <Box p="md" pl="xl">
              <GroupMemberTable
                group={group}
                device={device}
                highlightedStreamId={highlightedStreamId}
                renderMemberActions={renderMemberActions}
                onOpenGroup={onOpenGroup}
              />
            </Box>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  )
}
